import { z } from 'zod';
import { optionalPoint } from './geo.js';
/** What a saved place is. Only airports and other places may show as buttons on a driver's public page. */
export const PLACE_KINDS = ['airport', 'home', 'work', 'friend', 'other'];
export const PAGE_PLACE_KINDS = ['airport', 'other'];
export const MAX_PLACES = 20;
/** The empty buttons a new list offers, in this order. */
export const PLACE_SLOTS = [
    { kind: 'home', label: 'Home 1' },
    { kind: 'home', label: 'Home 2' },
    { kind: 'work', label: 'Work 1' },
    { kind: 'work', label: 'Work 2' },
    { kind: 'friend', label: 'Friend 1' },
    { kind: 'friend', label: 'Friend 2' },
];
/** Airports a Southern California driver or rider adds with one tap; the terminal loop addresses. */
export const AIRPORTS = [
    { code: 'LAX', label: 'LAX Airport', address: '1 World Way, Los Angeles, CA 90045', point: { lat: 33.9416, lng: -118.4085 } },
    { code: 'SNA', label: 'Santa Ana Airport', address: '18601 Airport Way, Santa Ana, CA 92707', point: { lat: 33.6762, lng: -117.8675 } },
    { code: 'LGB', label: 'Long Beach Airport', address: '4100 Donald Douglas Dr, Long Beach, CA 90808', point: { lat: 33.8177, lng: -118.1516 } },
    { code: 'BUR', label: 'Burbank Airport', address: '2627 N Hollywood Way, Burbank, CA 91505', point: { lat: 34.2007, lng: -118.3587 } },
    { code: 'ONT', label: 'Ontario Airport', address: '2500 E Airport Dr, Ontario, CA 91761', point: { lat: 34.056, lng: -117.6012 } },
    { code: 'SAN', label: 'San Diego Airport', address: '3225 N Harbor Dr, San Diego, CA 92101', point: { lat: 32.7336, lng: -117.1897 } },
];
const metersBetween = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180;
    const h = Math.sin(rad(a.lat - b.lat) / 2) ** 2 + Math.cos(rad(b.lat)) * Math.cos(rad(a.lat)) * Math.sin(rad(a.lng - b.lng) / 2) ** 2;
    return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
};
/** The airport at a point, when the point is within about a mile of one we know. */
export function airportAt(p) {
    if (!p)
        return null;
    return AIRPORTS.find((a) => metersBetween(a.point, p) < 1600) ?? null;
}
/**
 * What people call a place, for a service's name: a major airport, otherwise the city in the address.
 * Null when neither can be told, such as a street typed with no city.
 */
export function placeName(address, point) {
    const known = airportAt(point) ?? AIRPORTS.find((a) => a.address === address.trim() || new RegExp(`\\b${a.code}\\b`).test(address));
    if (known)
        return known.label;
    const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
    const airport = parts.find((p) => /\bairport\b/i.test(p));
    if (airport)
        return airport;
    // "..., Irvine, CA 92618": the part before the state.
    const state = parts.findLastIndex((p) => /^([A-Z]{2}|California)(\s+\d{5}(-\d{4})?)?$/.test(p));
    return state > 0 ? parts[state - 1] : null;
}
/** A service's name from its two places, as "LAX Airport → Irvine"; null until both can be named. */
export function serviceNameFor(pickup, dropoff) {
    const from = placeName(pickup.address, pickup.point);
    const to = placeName(dropoff.address, dropoff.point);
    return from && to ? `${from} → ${to}`.slice(0, 80) : null;
}
const samePlace = (a, aPoint, b, bPoint) => {
    if (aPoint && bPoint)
        return metersBetween(aPoint, bPoint) < 1000;
    const words = (s) => (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return words(a) !== '' && words(a) === words(b);
};
/** The driver's saved service for this trip: both places it names match, by the map when known, otherwise by the words. */
export function matchingService(services, trip) {
    return (services.find((s) => (s.pickupAddress || s.dropoffAddress) &&
        (!s.pickupAddress || samePlace(s.pickupAddress, s.pickupPoint, trip.pickupAddress, trip.pickupPoint)) &&
        (!s.dropoffAddress || samePlace(s.dropoffAddress, s.dropoffPoint, trip.dropoffAddress, trip.dropoffPoint))) ?? null);
}
/** A trip to or from an airport, by the name or code in an address or by where its places are: the airport fee applies. */
export const airportTrip = (stops) => stops.some((s) => /airport|\b(LAX|SNA|LGB|BUR|ONT|SAN)\b/i.test(s.address) || airportAt(s.point) !== null);
export const PlaceSchema = z
    .object({
    kind: z.enum(PLACE_KINDS),
    label: z.string().trim().min(1).max(40),
    /** The same rule as a booking's pickup and drop-off. */
    address: z.string().trim().min(3).max(300),
    /** Where the address is, when it was picked from a suggestion. */
    point: optionalPoint,
    showOnPage: z.boolean().optional().default(false),
})
    .refine((p) => !p.showOnPage || PAGE_PLACE_KINDS.includes(p.kind), {
    message: 'only airports and other places can show on your booking page',
    path: ['showOnPage'],
});
/** The whole list at once, in the order the buttons show. */
export const PlacesInputSchema = z.object({
    places: z
        .array(PlaceSchema)
        .max(MAX_PLACES, `up to ${MAX_PLACES} places`)
        .refine((list) => new Set(list.map((p) => p.label.toLowerCase())).size === list.length, {
        message: 'each place needs its own name',
    }),
});
/** The address a passenger shares with drivers: their first home place. */
export const homeAddressOf = (places) => places.find((p) => p.kind === 'home')?.address ?? null;
//# sourceMappingURL=places.js.map